import { ValidateOtpResponseDto } from "../../interfaces/ExtermalUserDtos/ExternalUsersDtos";
import { ApiResponse } from "../../interfaces/genericResponse/GenericResponse";
import { CallActionRequest, CreateMeetingRoomRequest, CreateMeetingRoomResponse, InitiateCallRequest, InitiateCallResponse, JanusHangupRequest, MeetingRoomDto } from "../../interfaces/videocallDtos/videoCall";
import { Conversation } from "../chatService/ChatService";

export interface Publisher {
    feedId: string;
    display: string;
    userId?: string;
    isMuted?: boolean;
    isCameraOn?: boolean;
  }

export interface MeetingControlParticipant {
    userId: string;
    displayName: string;
    isExternal: boolean;
    isRemoved: boolean;
    feedId?: string;
    isMuted: boolean;
    status?: "calling" | "did_not_answer" | "joined" | "left" | "removed" | "invited";
    invitedAt?: string;
}

export interface MeetingInviteCandidate {
    userId: string;
    displayName: string;
    avatarUrl?: string;
    scId?: string;
}

export interface MeetingControlState {
    roomId: string;
    meetingId?: string;
    hostUserId: string;
    isHost: boolean;
    isEnded: boolean;
    participants: MeetingControlParticipant[];
}

export type MeetingAdmissionStatus = "active" | "removed" | "ended";

export interface MeetingAdmissionState {
    roomId: string;
    status: MeetingAdmissionStatus;
}

export class MeetingSignalError extends Error {
    constructor(message: string, public readonly status: number) { super(message); }
}
  
  export interface SessionResponse {
    sessionId: number;
    handleId: number;
    sdpAnswer: string;
    feedId: string;
    privateId: number;
    // NEW: Includes the list of participants already in the room
    // to prevent a race condition on the client's initial sync.
    otherPublishers?: Publisher[];
  }

// export interface TrickleRequest {
//     sessionId: number;
//     handleId: number;
//     candidate: RTCIceCandidate;
// }

export interface TrickleCandidateDto {
    candidate: string;
    sdpMid?: string | null;
    sdpMLineIndex?: number | null;
  }
  
  export interface TrickleRequest {
    sessionId: number;
    handleId: number;
  
    // One of the two is present:
    candidate?: TrickleCandidateDto; // normal trickle
    candidates?: TrickleCandidateDto[]; // ordered batch
    completed?: boolean;             // end-of-candidates
  }
  

export interface HangupRequest {
    sessionId: number;
    handleId: number;
    roomId: string;
}

export interface SubscribeRequest {
    sessionId: number;
    handleId?: number;
    roomId: string | undefined;
    feedId: string;
    sdpOffer?: string;
}

export interface SubscribeResponse {
    subscriberHandleId?: number;
    receivedOffer?: string;
    needsAnswer?: boolean;
    sdpAnswer?: string;
}

const BASE_URL = `${process.env.REACT_APP_API_BASE_URL}/video`;
const FALLBACK_ICE_SERVERS: RTCIceServer[] = [{ urls: "stun:stun.l.google.com:19302" }];
let turnCache: { expiresAt: number; value: RTCIceServer[] } | null = null;
let turnInFlight: Promise<RTCIceServer[] | null> | null = null;
let joinAttemptId: string | null = null;
let joinAttemptStartedAt = 0;
let joinAttemptPendingNavigation = false;

export function beginMeetingTiming(reuseExisting = false): string {
    if (reuseExisting && joinAttemptId && joinAttemptPendingNavigation &&
        performance.now() - joinAttemptStartedAt < 60_000) {
        joinAttemptPendingNavigation = false;
        return joinAttemptId;
    }
    joinAttemptId = typeof crypto !== "undefined" && "randomUUID" in crypto
        ? crypto.randomUUID()
        : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
    joinAttemptStartedAt = performance.now();
    joinAttemptPendingNavigation = !reuseExisting;
    markMeetingTiming("join.begin");
    return joinAttemptId;
}

export function markMeetingTiming(stage: string, details: Record<string, string | number | boolean> = {}): void {
    if (!joinAttemptId) return;
    console.info("[meeting-timing]", JSON.stringify({
        attemptId: joinAttemptId, stage,
        elapsedMs: Math.round(performance.now() - joinAttemptStartedAt), ...details,
    }));
}

function signalingHeaders(authToken: string): Record<string, string> {
    const headers = GetHeaders(authToken);
    if (joinAttemptId) headers["X-Meeting-Trace-Id"] = joinAttemptId;
    return headers;
}

async function hostControlRequest(path: string, authToken: string, body: unknown): Promise<Response> {
    const response = await fetchVideoSignal(`${BASE_URL}/${path}`, {
        method: "POST", headers: GetHeaders(authToken), body: JSON.stringify(body),
    });
    if (!response.ok) throw new Error((await response.text()) || `Meeting action failed (${response.status})`);
    return response;
}

export async function getMeetingControlState(authToken: string, roomId: string): Promise<MeetingControlState> {
    const response = await fetchVideoSignal(`${BASE_URL}/room/${encodeURIComponent(roomId)}/control-state`, {
        method: "GET", headers: GetHeaders(authToken),
    });
    if (!response.ok) throw new Error((await response.text()) || "Meeting state is unavailable");
    return response.json();
}

export async function getMeetingAdmissionState(authToken: string, roomId: string): Promise<MeetingAdmissionState> {
    const response = await fetchVideoSignal(`${BASE_URL}/room/${encodeURIComponent(roomId)}/admission-state`, {
        method: "GET", headers: GetHeaders(authToken),
    });
    if (!response.ok) throw new MeetingSignalError(
        (await response.text()) || "Meeting admission state is unavailable",
        response.status,
    );
    return response.json();
}

export async function getMeetingInviteCandidates(authToken: string, roomId: string): Promise<MeetingInviteCandidate[]> {
    const response = await fetchVideoSignal(`${BASE_URL}/room/${encodeURIComponent(roomId)}/invite-candidates`, {
        method: "GET", headers: GetHeaders(authToken),
    });
    if (!response.ok) throw new Error((await response.text()) || "Employees are unavailable");
    return response.json();
}

export async function hostInviteParticipant(authToken: string, roomId: string, target: { targetUserId?: string; externalEmail?: string }) {
    const response = await hostControlRequest("host/invite", authToken, { roomId, ...target });
    return response.json();
}

export async function hostMuteParticipant(authToken: string, roomId: string, targetUserId: string) {
    await hostControlRequest("host/mute", authToken, { roomId, targetUserId });
}

export async function hostRemoveParticipant(authToken: string, roomId: string, targetUserId: string) {
    await hostControlRequest("host/remove", authToken, { roomId, targetUserId });
}

export async function setPublisherAudio(authToken: string, roomId: string, muted: boolean) {
    await hostControlRequest("publisher/audio", authToken, { roomId, muted });
}

export async function endMeetingForEveryone(authToken: string, roomId: string, meetingId?: string) {
    await hostControlRequest("global-hangup", authToken, { roomId, meetingId });
}


export const GetHeaders = (authToken: string, isJson: boolean = true): Record<string, string> => {
      const headers: Record<string, string> = {};
    if (isJson) {
        headers["Content-Type"] = "application/json";
    }
    const ex_details = localStorage.getItem("ExternalUserDetails");
    if (ex_details && (!authToken || authToken === "")) {
        const externalUserDetails: ValidateOtpResponseDto = JSON.parse(ex_details);
        headers["X-Video-Token"] = externalUserDetails.tempSessionToken;
        headers["X-External-TicketId"] = externalUserDetails.ticketId;
        headers["X-External_UserId"] = externalUserDetails.externalUserDetailId;
    } else {
        headers["Authorization"] = `Bearer ${authToken}`;
    }

    return headers;
};


/**
 * Sends the SDP offer to the backend, receives SDP answer from Janus via backend
 */
export async function sendOffer(
    displayName: string,
    roomId: string | undefined,
    sdpOffer: string,
    authToken: string,
    isMuted: boolean,    
    isCameraOn: boolean, 
    sessionId?: number | null,
    userId?: string | null,
): Promise<SessionResponse | null> {
    try {
        const response = await fetchVideoSignal(`${BASE_URL}/offer`, {
            method: "POST",
            headers: GetHeaders(authToken),
            body: JSON.stringify({ displayName, roomId, sdpOffer, sessionId, isMuted, isCameraOn, userId }), // Ensure DTO matches backend
        });

        if (!response.ok) return null;
        const responseBodyText = await response.text();
        if (!responseBodyText) {
            console.error("[sendOffer] Backend response body is empty.");
            return null;
        }
        try {
            const data: SessionResponse = JSON.parse(responseBodyText);
            if (
                !Number.isSafeInteger(data.sessionId) || data.sessionId <= 0 ||
                !Number.isSafeInteger(data.handleId) || data.handleId <= 0 ||
                typeof data.feedId !== "string" || !data.feedId ||
                !Number.isSafeInteger(data.privateId) || data.privateId <= 0 ||
                typeof data.sdpAnswer !== "string" || !data.sdpAnswer.startsWith("v=0") || data.sdpAnswer.length > 262144
            ) {
                return null;
            }
            return data;
        } catch (e) {
            return null;
        }
    } catch (err) {
        console.error("[sendOffer] Network or other error:", err);
        return null;
    }
}

function normalizeIceCandidate(
    ice: RTCIceCandidate | RTCIceCandidateInit
  ): TrickleCandidateDto | null {
    // Browser might pass RTCIceCandidate or the init shape
    const cand = "candidate" in ice ? ice.candidate : (ice as any)?.candidate;
    if (!cand) return null;
  
    const sdpMid =
      "sdpMid" in ice ? (ice as any).sdpMid ?? null : (ice as any)?.sdpMid ?? null;
    const sdpMLineIndex =
      "sdpMLineIndex" in ice
        ? (ice as any).sdpMLineIndex ?? null
        : (ice as any)?.sdpMLineIndex ?? null;
  
    return {
      candidate: cand,
      sdpMid,
      sdpMLineIndex,
    };
  }
  

/**
 * Sends a single ICE candidate to the backend for Janus trickle forwarding.
 * Accepts either RTCIceCandidate or RTCIceCandidateInit.
 */
export async function sendTrickleCandidate(
    sessionId: number,
    handleId: number,
    ice: RTCIceCandidate | RTCIceCandidateInit,
    authToken: string
  ): Promise<void> {
    try {
      const candidate = normalizeIceCandidate(ice);
      if (!candidate) {
        console.warn("[sendTrickleCandidate] Ignored empty/null candidate");
        return;
      }
  
      const req: TrickleRequest = {
        sessionId,
        handleId,
        candidate,
      };
  
      const response = await fetchVideoSignal(`${BASE_URL}/trickle`, {
        method: "POST",
        headers: GetHeaders(authToken),
        body: JSON.stringify(req),
      });
      if (!response.ok) throw new Error(`ICE signaling failed (${response.status})`);
    } catch (err) {
      throw err;
    }
  }

  /**
 * Tells Janus we're done trickling for this PC.
 */
export async function sendTrickleCompleted(
    sessionId: number,
    handleId: number,
    authToken: string
  ): Promise<void> {
    try {
      const req: TrickleRequest = {
        sessionId,
        handleId,
        completed: true,
      };
  
      const response = await fetchVideoSignal(`${BASE_URL}/trickle`, {
        method: "POST",
        headers: signalingHeaders(authToken),
        body: JSON.stringify(req),
      });
      if (!response.ok) throw new Error(`ICE signaling failed (${response.status})`);
    } catch (err) {
      throw err;
    }
  }
  
  
// export async function hangup(
//   req: HangupRequest,
//   authToken: string
// ): Promise<void> {
//   // Add authToken
//   try {
//     await fetch(`${BASE_URL}/hangup`, {
//       method: "POST",
//       headers: {
//         "Content-Type": "application/json",
//         Authorization: `Bearer ${authToken}`, // <<< ADD THIS
//       },
//       body: JSON.stringify(req),
//     });
//     console.log("[hangup] Hangup request sent.");
//   } catch (err) {
//     console.error("[hangup] Failed to send hangup request:", err);
//   }
// }

export async function subscribeToFeed(
    authToken: string,
    request: SubscribeRequest
): Promise<SubscribeResponse | null> {
    try {
        const response = await fetchVideoSignal(`${BASE_URL}/subscribe-feed`, {
            method: "POST",
            headers: GetHeaders(authToken),
            body: JSON.stringify(request),
        });

        if (!response.ok) {
            console.error("Subscriber negotiation failed", response.status);
            return null;
        }
        const data: SubscribeResponse = await response.json();
        if (!data || (data.subscriberHandleId !== undefined &&
            (!Number.isSafeInteger(data.subscriberHandleId) || data.subscriberHandleId <= 0)) ||
            (data.needsAnswer !== undefined && typeof data.needsAnswer !== "boolean") ||
            (data.receivedOffer !== undefined && (typeof data.receivedOffer !== "string" || data.receivedOffer.length > 262144))) return null;
        return data;
    } catch (err) {
        console.error(`[subscribeToFeed] Failed to subscribe to remote feed. FeedId: ${request.feedId} | SessionId: ${request.sessionId}`, err);
        return null;
    }
}

export async function sendTrickleBatch(
    sessionId: number,
    handleId: number,
    ice: Array<RTCIceCandidate | RTCIceCandidateInit>,
    authToken: string
): Promise<void> {
    const candidates = ice.map(normalizeIceCandidate).filter((candidate): candidate is TrickleCandidateDto => candidate !== null);
    if (!candidates.length) return;
    if (candidates.length > 128) throw new Error("ICE candidate batch exceeds limit");
    const response = await fetchVideoSignal(`${BASE_URL}/trickle`, {
        method: "POST",
        headers: signalingHeaders(authToken),
        body: JSON.stringify({ sessionId, handleId, candidates } satisfies TrickleRequest),
    });
    if (!response.ok) throw new Error(`ICE signaling failed (${response.status})`);
}

export async function getPublishers(authToken: string, roomId: string | undefined): Promise<Publisher[]> {
    const response = await fetchVideoSignal(`${BASE_URL}/room/${encodeURIComponent(roomId ?? "")}/publishers`, { headers: GetHeaders(authToken) });
    if (!response.ok) throw new Error("Publisher reconciliation unavailable");
    const data: unknown = await response.json();
    if (!Array.isArray(data) || data.length > 128 || data.some(p => !p || typeof p.feedId !== "string" || typeof p.display !== "string")) {
        throw new Error("Invalid publisher snapshot");
    }
    return data;
}

export async function hangup(sessionId: number, handleId: number, roomId: string, token: string, isHangupScreen :boolean = false,conversationId? : number, isCallRejectedHangUp : boolean = false) {
    const payload :JanusHangupRequest = {
        sessionId: sessionId,
        handleId: handleId,
        roomId: roomId,
        isHangupScreen: isHangupScreen,
        conversationId: conversationId,
        isCallRejectedHangUp: isCallRejectedHangUp
    }
    const res = await fetch(`${BASE_URL}/hangup`, {
        method: "POST",
        headers: GetHeaders(token),
        body: JSON.stringify(payload),
    });

    if (!res.ok) {
        const err = await res.text();
        throw new Error(`Failed to hang up session: ${err}`);
    }

    return res.json();
}

export async function hangupScreen(sessionId: number, handleId: number, roomId: string, token: string, isHangupScreen :boolean = false) : Promise<any> {
    const payload :JanusHangupRequest = {
        sessionId: sessionId,
        handleId: handleId,
        roomId: roomId,
        isHangupScreen: isHangupScreen,
        isCallRejectedHangUp: false
    }
    const res = await fetch(`${BASE_URL}/hangup`, {
        method: "POST",
        headers: GetHeaders(token),
        body: JSON.stringify(payload),
    });

    if (!res.ok) {
        const err = await res.text();
        throw new Error(`Failed to hang up session: ${err}`);
    }
    return true;
}

export async function getTurnCredentials(authToken: string): Promise<RTCIceServer[] | null> {
    if (turnCache && turnCache.expiresAt > Date.now()) {
        markMeetingTiming("turn.ready", { durationMs: 0, cached: true });
        return turnCache.value;
    }
    if (turnInFlight) return turnInFlight;
    turnInFlight = (async () => {
        const controller = new AbortController();
        const timer = setTimeout(() => controller.abort(), 4000);
        const startedAt = performance.now();
        try {
            const response = await fetch(`${BASE_URL}/turn-credentials`, {
                method: "POST", headers: signalingHeaders(authToken), signal: controller.signal,
            });
            if (!response.ok) throw new Error(`TURN credential request failed (${response.status})`);
            const iceServers: RTCIceServer[] = await response.json();
            if (!Array.isArray(iceServers) || iceServers.length === 0) throw new Error("TURN response was empty");
            turnCache = { value: iceServers, expiresAt: Date.now() + 45 * 60 * 1000 };
            markMeetingTiming("turn.ready", { durationMs: Math.round(performance.now() - startedAt), cached: false });
            return iceServers;
        } catch (err) {
            markMeetingTiming("turn.fallback", { durationMs: Math.round(performance.now() - startedAt) });
            console.warn("[getTurnCredentials] TURN unavailable; using STUN fallback.");
            if (turnCache && turnCache.expiresAt > Date.now()) return turnCache.value;
            turnCache = null;
            return FALLBACK_ICE_SERVERS;
        } finally {
            clearTimeout(timer);
            turnInFlight = null;
        }
    })();
    return turnInFlight;
}

/**
 * Sends a new SDP offer to an existing publisher handle for renegotiation (ICE Restart).
 * Backend returns the JSEP answer from Janus.
 */
export async function configurePublisher(
    sessionId: number,
    handleId: number,
    sdpOffer: string,
    authToken: string
  ): Promise<{ sdpAnswer: string } | null> {
    try {
      const response = await fetch(`${BASE_URL}/publisher/renegotiate`, {
        method: "POST",
        headers: GetHeaders(authToken),
        body: JSON.stringify({ sessionId, handleId, sdpOffer }),
      });
  
      if (!response.ok) {
        console.error("Publisher renegotiation failed", response.status);
        return null;
      }
  
      return await response.json();
    } catch (err) {
      console.error("[configurePublisher] Network error:", err);
      return null;
    }
  }
  

/**
 * Sends a keep-alive ping to the Janus session to prevent it from timing out.
 */
export async function keepAlive(sessionId: number, authToken: string): Promise<void> {
    try {
        await fetch(`${BASE_URL}/keepalive`, {
            method: "POST",
            headers: GetHeaders(authToken),
            body: JSON.stringify({ sessionId }),
        });
    } catch (err) {
        // It's okay to silently fail here, the next one will likely succeed.
        console.warn("[keepAlive] Failed to send keep-alive ping:", err);
    }
}

export const createNewRoom = async (authToken: string, description: string): Promise<{ roomId: string }> => {
    const response = await fetch(`${BASE_URL}/rooms`, {
        method: 'POST',
        headers: GetHeaders(authToken),
        body: JSON.stringify({ description }),
    });
    if (!response.ok) {
        throw new Error('Failed to create a new room');
    }
    return response.json();
};


export const GetExistingOrCretateMeetingAsync = async (
    authToken: string,
    calendereventId: string | null = null,
    conversationId: string | null = null,
): Promise<ApiResponse<CreateMeetingRoomResponse>> => {
    try {
        const payload: CreateMeetingRoomRequest = {
            calendarEventId: calendereventId,
            conversationId: Number(conversationId)
        }
        const apiUrl = `${BASE_URL}/createmeetingroom`
        // Adjust the base URL if needed
        const response = await fetchVideoSignal(apiUrl,
            {
                method: "POST",
                headers: signalingHeaders(authToken),
                body: JSON.stringify(payload)
            }
        );
        return await response.json();
    } catch (error) {
        throw error;
    }
}

export const InitiateVideoCallNotifyAsync = async (
    authToken: string,
    payload: InitiateCallRequest,
): Promise<InitiateCallResponse> => {
    try {

        const apiUrl = `${BASE_URL}/initiate`
        // Adjust the base URL if needed
        const response = await fetchVideoSignal(apiUrl,
            {
                method: "POST",
                headers: signalingHeaders(authToken),
                body: JSON.stringify(payload)
            }
        );
        if (!response.ok) {
            throw new Error((await response.text()) || `Call initiation failed (${response.status})`);
        }
        return await response.json();
    } catch (error) {
        throw error;
    }
}

export interface ActiveCallStartResponse {
    roomId: string;
    startTimeUnixMs: number;
}

/** Gets persisted CallRecord.StartTime for the active room. */
export const GetActiveCallStartAsync = async (
    authToken: string,
    roomId: string,
): Promise<ActiveCallStartResponse | null> => {
    try {
        const response = await fetchVideoSignal(`${BASE_URL}/active-call-start/${encodeURIComponent(roomId)}`, {
            method: "GET",
            headers: GetHeaders(authToken),
        });

        if (response.status === 404) return null;
        if (!response.ok) {
            console.warn("Failed to fetch authoritative call start time:", response.status);
            return null;
        }

        const result = await response.json();
        const startTimeUnixMs = Number(result?.startTimeUnixMs);
        if (!Number.isFinite(startTimeUnixMs) || startTimeUnixMs <= 0) return null;

        return { roomId: String(result?.roomId ?? roomId), startTimeUnixMs };
    } catch (error) {
        console.warn("Unable to fetch authoritative call start time:", error);
        return null;
    }
};

export const GetMeetingByAssignedIdAsync = async (
    authToken: string,
    roomId: string,
    videoToken: string = "",
    externalTicketId: string = "",
    externalUserId: string = ""
): Promise<ApiResponse<MeetingRoomDto>> => {
    try {
        const apiUrl = `${BASE_URL}/getmeetingbyAssignedId/${roomId}`
        // Adjust the base URL if needed
        const response = await fetch(apiUrl,
            {
                method: "GET",
                headers: GetHeaders(authToken),
            }
        );
        const result = await response.json();
        return result;
    } catch (error) {
        throw error;
    }
}

// for saving Record Only
export const AcceptVideoCallAsync = async (
    authToken: string,
    payload: CallActionRequest,
): Promise<"accepted" | "pending" | "rejected"> => {
    try {

        const apiUrl = `${BASE_URL}/accept`
        // Adjust the base URL if needed
        const response = await fetchVideoSignal(apiUrl,
            {
                method: "POST",
                headers: GetHeaders(authToken),
                body: JSON.stringify(payload)
            }
        );

        if (response.ok) {
            return "accepted";
        }
        if (response.status === 409) {
            const result = await response.json();
            if (result?.code === "CALL_NOT_READY") return "pending";
        }
        return "rejected";
    } catch (error) {
        throw error;
    }
}

// for saving Record Only
export const RejectVideoCallAsync = async (
    authToken: string,
    payload: CallActionRequest,
): Promise<any | null> => {
    try {
        const apiUrl = `${BASE_URL}/reject`
        // Adjust the base URL if needed
        const response = await fetch(apiUrl,
            {
                method: "POST",
                headers: GetHeaders(authToken),
                body: JSON.stringify(payload)
            }
        );
        if (response.ok) {
            return true;
        }
        return false;
    } catch (error) {
        throw error;
    }
}

export const GetorCreateConversationForEventAsync = async (
    authToken: string,
    calendarEventId: string,
): Promise<ApiResponse<Conversation>> => {
    try {
        const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/chat/getorcreate/conversationforEvent/${calendarEventId}`
        // Adjust the base URL if needed
        const response = await fetch(apiUrl,
            {
                method: "GET",
                headers: GetHeaders(authToken),
            }
        );

        return await response.json();
    } catch (error) {
        throw error;
    }
}

/** 
 * Gets a single-use hangup token from the backend for a specific session/handle.
 * This token is safe to use in a URL for navigator.sendBeacon.
 * @param sessionId The Janus session ID.
 * @param handleId The specific Janus handle ID to be hung up.
 * @param roomId The room ID associated with the session.
 * @param authToken The user's primary Firebase auth token.
 * @returns An object containing the hangupToken, or null if it fails.
 */
export async function getHangupToken(
    sessionId: number,
    handleId: number,
    roomId: string,
    authToken: string
  ): Promise<{ hangupToken: string } | null> {
    try {
      const response = await fetch(`${BASE_URL}/hangup-token`, {
        method: "POST",
        headers: GetHeaders(authToken),
        body: JSON.stringify({ sessionId, handleId, roomId }),
      });
      if (!response.ok) {
          console.error("Failed to get hangup token, status:", response.status);
          return null;
      }
      return response.json();
    } catch (err) {
      console.error("[getHangupToken] Network error:", err);
      return null;
    }
  }

/**
 * Sends a hangup request using navigator.sendBeacon for reliable teardown on page unload.
 * NOTE: This requires a backend endpoint that can accept the auth token as a query parameter,
 * as sendBeacon cannot set Authorization headers.
 * @param {HangupRequest} req - The hangup request payload.
 * @param {string} token - The user's auth token.
 */
export function hangupWithBeacon(req: HangupRequest, token: string): void {
    // IMPORTANT: You need a dedicated backend endpoint for this.
    // Example: POST /video/hangup-beacon?authToken={token}
    const url = `${BASE_URL}/hangup-beacon?token=${encodeURIComponent(token)}`;
    const blob = new Blob([JSON.stringify(req)], { type: 'application/json; charset=UTF-8' });
  
    // Use sendBeacon for reliable "fire-and-forget" delivery
    if (!navigator.sendBeacon(url, blob)) {
      console.error("[hangupWithBeacon] Failed to queue beacon request.");
      // As a fallback for older browsers, you could potentially use a synchronous XHR,
      // but this is discouraged as it blocks the UI thread.
    } else {
      console.log("[hangupWithBeacon] Beacon hangup request queued for:", req);
    }
  }

export function getExternalUserDetails(): ValidateOtpResponseDto | null {
    const raw = localStorage.getItem("ExternalUserDetails");
    return raw ? JSON.parse(raw) as ValidateOtpResponseDto : null;
}
export async function detachSubscriber(sessionId: number, handleId: number, token: string) {
    const response = await fetch(`${BASE_URL}/detach-subscriber`, {
        method: "POST", headers: GetHeaders(token), body: JSON.stringify({ sessionId, handleId }),
    });
    if (!response.ok) throw new Error("Subscriber cleanup failed");
}

async function fetchVideoSignal(url: string, init: RequestInit): Promise<Response> {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 10000);
    const headers = new Headers(init.headers);
    if (joinAttemptId) headers.set("X-Meeting-Trace-Id", joinAttemptId);
    const startedAt = performance.now();
    try {
        const response = await fetch(url, { ...init, headers, signal: controller.signal });
        markMeetingTiming("signal.response", { endpoint: new URL(url).pathname.split("/").pop() ?? "unknown", durationMs: Math.round(performance.now() - startedAt), status: response.status });
        return response;
    }
    finally { clearTimeout(timer); }
}
